import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { WorkModel } from "../../../factories/works/models/work.model";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable, filtersText } from "../ask/reportAsk.utils";
import {
  budgetByWorkType,
  budgetRows,
  formatHours,
  peopleOf,
} from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import {
  ChoiceMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  AssigneeCell,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  Legend,
  LoadError,
  Loading,
  MiniBar,
  NUM,
  ReportPage,
  TableBox,
  TD,
  TH,
  useFocusRows,
  WorkLink,
} from "../ui/ReportUi";
import { useAllWorks } from "../useReportData";

type Scope = "open" | "all" | "completed";
const SCOPES: { value: Scope; label: string }[] = [
  { value: "open", label: "Open work" },
  { value: "completed", label: "Completed work" },
  { value: "all", label: "Open and completed" },
];

/** The assignee as the table shows it (see AssigneeCell), for "Ask about this report". */
const assigneeText = (w: WorkModel) => {
  const people = peopleOf(w);
  if (!people[0]?.id) {
    return w.roleName ? `Role: ${w.roleName}` : "Not assigned";
  }
  return people.map((p) => p.name).join(", ");
};

const hoursText = (h: number) => formatHours(h);

export const BudgetReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters();
  const { filters } = state;
  const [scope, setScope] = useViewExtra<Scope>(
    "scope",
    "open",
    SCOPES,
    "Work",
  );
  const works = useAllWorks({
    hideGreenStatus: scope === "open",
    workStatuses: scope === "completed" ? ["Completed"] : [],
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    worktypeIds: filters.workTypeIds,
  });
  const rows = useMemo(() => budgetRows(works.data), [works.data]);
  const byType = useMemo(() => budgetByWorkType(rows), [rows]);
  const budget = rows.reduce((s, r) => s + r.budget, 0);
  const tracked = rows.reduce((s, r) => s + r.tracked, 0);
  const over = rows.filter((r) => r.diff > 0.004).length;
  const noBudget = works.data.length - rows.length;
  const scale = Math.max(
    1,
    ...byType.map((t) => Math.max(t.budget, t.tracked)),
  );
  const v = (x: React.ReactNode) => (works.isLoading ? "…" : x);

  const onExport = () =>
    exportDataToExcel(
      rows.map((r) => ({
        Work: r.work.workName,
        Client: r.work.customerName,
        "Work type": r.work.workType,
        Assignee: peopleOf(r.work)
          .map((p) => p.name)
          .join(", "),
        "Budgeted hours": Math.round(r.budget * 10) / 10,
        "Tracked hours": Math.round(r.tracked * 10) / 10,
        "Difference (hours)": Math.round(r.diff * 10) / 10,
        "Budget used %": r.pct,
      })),
      "Budget vs actual hours",
    );

  const tableName = "Work items, most over budget first";
  const shownRows = useFocusRows(tableName, rows, (r) => r.work.workId);
  const { pageRows, pager } = usePagedRows(shownRows);

  const ask = useMemo(
    () => ({
      title: "Budget vs actual hours",
      path: "/reports/budget",
      description:
        "The hours budgeted on each work item compared with the hours tracked on it. Work without a budget is left out.",
      filters: filtersText([
        `Work: ${SCOPES.find((s) => s.value === scope)?.label}`,
        !access.firmWide && "Only your own work",
      ]),
      kpis: [
        { label: "Budgeted", value: hoursText(budget) },
        {
          label: "Tracked",
          value: budget
            ? `${hoursText(tracked)} (${Math.round(
                (tracked / budget) * 100,
              )}% of the budget)`
            : hoursText(tracked),
        },
        { label: "Over budget (work items)", value: String(over) },
        {
          label: "No budget set (work items, not counted)",
          value: String(noBudget),
        },
      ],
      tables: [
        askTable(
          "By work type",
          byType,
          [
            {
              key: "workType",
              label: "Work type",
              kind: "text",
              value: (t) => t.name,
            },
            {
              key: "budget",
              label: "Budgeted hours",
              kind: "hours",
              value: (t) => t.budget,
            },
            {
              key: "tracked",
              label: "Tracked hours",
              kind: "hours",
              value: (t) => t.tracked,
            },
          ],
          (t) => t.name,
        ),
        askTable(
          tableName,
          rows,
          [
            {
              key: "work",
              label: "Work",
              kind: "text",
              value: (r) => r.work.workName,
            },
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (r) => r.work.customerName,
            },
            {
              key: "assignee",
              label: "Assignee",
              kind: "text",
              value: (r) => assigneeText(r.work),
            },
            {
              key: "budget",
              label: "Budgeted hours",
              kind: "hours",
              value: (r) => r.budget,
            },
            {
              key: "tracked",
              label: "Tracked hours",
              kind: "hours",
              value: (r) => r.tracked,
            },
            {
              key: "diff",
              label: "Difference (hours)",
              kind: "hours",
              value: (r) => r.diff,
            },
            {
              key: "pct",
              label: "Budget used",
              kind: "percent",
              value: (r) => r.pct,
            },
          ],
          (r) => r.work.workId,
          (r) => `/work/${r.work.workId}`,
        ),
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, byType, works.data, scope, access.firmWide],
  );

  return (
    <ReportPage
      title={"Budget vs actual hours"}
      description={
        "The hours budgeted on each work item compared with the hours tracked on it, most over budget first."
      }
      onExport={rows.length ? onExport : undefined}
      ask={works.isLoading ? undefined : ask}
      askSuggestions={[
        "Which work is most over budget?",
        "Which work type goes over budget most?",
        "How much budget is left on open work?",
      ]}
      filters={
        <>
          <ChoiceMenu
            label={"Work"}
            options={SCOPES}
            value={scope}
            onChange={setScope}
          />
          <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
        </>
      }>
      {works.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Budgeted"}
          value={v(hoursText(budget))}
          note={"On work with a budget"}
        />
        <Kpi
          label={"Tracked"}
          value={v(hoursText(tracked))}
          note={
            budget
              ? `${Math.round((tracked / budget) * 100)}% of the budget`
              : undefined
          }
        />
        <Kpi
          label={"Over budget"}
          value={v(over)}
          note={"Work items"}
          tone={"red"}
        />
        <Kpi
          label={"No budget set"}
          value={v(noBudget)}
          note={"Work items not counted here"}
        />
      </KpiGrid>

      <Card
        title={"By work type"}
        right={
          <Legend
            items={[
              { label: "Budgeted", color: "#C9CEDA" },
              { label: "Tracked", color: "#6B4EFF" },
              { label: "Tracked, over budget", color: "#D92D20" },
            ]}
          />
        }>
        {works.isLoading ? (
          <Loading />
        ) : works.isError ? null : !byType.length ? (
          <Empty text={"No work with a budget."} />
        ) : (
          <div className={"flex flex-col gap-3.5"}>
            {byType.map((t) => (
              <div
                key={t.name}
                className={"grid items-center gap-3.5"}
                style={{
                  gridTemplateColumns:
                    "minmax(110px, 160px) minmax(0, 1fr) 140px",
                }}>
                <span className={"truncate text-[13.5px] font-bold"}>
                  {t.name}
                </span>
                <div
                  className={"flex flex-col gap-1"}
                  role={"img"}
                  aria-label={`${t.name}: ${hoursText(
                    t.tracked,
                  )} tracked of ${hoursText(t.budget)} budgeted`}>
                  <div
                    className={"h-2.5 rounded-full bg-[#C9CEDA]"}
                    style={{ width: `${(t.budget / scale) * 100}%` }}
                  />
                  <div
                    className={"h-2.5 rounded-full"}
                    style={{
                      width: `${(t.tracked / scale) * 100}%`,
                      backgroundColor:
                        t.tracked > t.budget ? "#D92D20" : "#6B4EFF",
                    }}
                  />
                </div>
                <span className={"text-right text-[13px] font-bold"}>
                  {hoursText(t.tracked)} of {hoursText(t.budget)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title={"Work items, most over budget first"}>
        {works.isLoading ? (
          <Loading />
        ) : works.isError ? null : !rows.length ? (
          <Empty text={"No work with a budget."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Work
                </th>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={TH}>
                  Assignee
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Budgeted
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Tracked
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Difference
                </th>
                <th scope={"col"} className={TH}>
                  Budget used
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const isOver = r.diff > 0.004;
                return (
                  <tr key={r.work.workId}>
                    <td className={TD}>
                      <WorkLink id={r.work.workId} name={r.work.workName} />
                    </td>
                    <td className={TD}>{r.work.customerName}</td>
                    <td className={TD}>
                      <AssigneeCell
                        people={peopleOf(r.work)}
                        role={r.work.roleName}
                      />
                    </td>
                    <td className={`${TD} ${NUM}`}>{hoursText(r.budget)}</td>
                    <td className={`${TD} ${NUM}`}>{hoursText(r.tracked)}</td>
                    <td
                      className={`${TD} ${NUM} font-extrabold ${
                        isOver ? "text-[#B42318]" : "text-[#166534]"
                      }`}>
                      {isOver ? "+" : ""}
                      {hoursText(r.diff)}
                    </td>
                    <td className={TD}>
                      <span className={"inline-block w-12 font-bold"}>
                        {r.pct}%
                      </span>
                      <MiniBar
                        pct={r.pct}
                        color={isOver ? "#D92D20" : "#16A34A"}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableBox>
        )}
      </Card>
    </ReportPage>
  );
};
